import { useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { changeAppLanguage } from '@/locales'
import { useModal, useSupportedLocales } from '@hooks'
import {
  getLocaleFromPath,
  getLocaleLabel,
  normalizeLocale,
  withLocale,
  type SupportedLocale,
} from '@utils'
import { ArrowDownIcon } from '../icons'
import * as S from './styled'

export function LanguageSelect() {
  const navigate = useNavigate()
  const { pathname, search, hash } = useLocation()
  const { i18n } = useTranslation()
  const locales = useSupportedLocales()
  const { isOpen, close, toggle } = useModal()
  const containerRef = useRef<HTMLDivElement>(null)
  const pathLocale = getLocaleFromPath(pathname)
  const current = pathLocale ?? normalizeLocale(i18n.language)

  useEffect(() => {
    if (!isOpen) return

    const handleOutsideClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) close()
    }
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }

    document.addEventListener('mousedown', handleOutsideClick)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, close])

  const handleSelect = async (next: SupportedLocale) => {
    close()
    if (next === current && pathLocale) return

    await changeAppLanguage(next)
    navigate(`${withLocale(pathname, next)}${search}${hash}`, { replace: true })
  }

  return (
    <S.LanguageSelectWrapper id={'language-select'} ref={containerRef}>
      <S.LanguageTrigger
        id={'language-select-button'}
        $open={isOpen}
        aria-label={'Language'}
        aria-haspopup={'listbox'}
        aria-expanded={isOpen}
        onClick={toggle}
      >
        {getLocaleLabel(current)}
        <S.LanguageChevron $open={isOpen}>
          <ArrowDownIcon />
        </S.LanguageChevron>
      </S.LanguageTrigger>
      <S.LanguageOptions id={'language-select-options'} role={'listbox'} $open={isOpen}>
        {locales.map((locale) => (
          <S.LanguageOption
            key={locale}
            id={`language-option-${locale}`}
            role={'option'}
            aria-selected={locale === current}
            className={locale === current ? 'active' : ''}
            onClick={() => void handleSelect(locale)}
          >
            {getLocaleLabel(locale)}
          </S.LanguageOption>
        ))}
      </S.LanguageOptions>
    </S.LanguageSelectWrapper>
  )
}
